import { useForm } from 'react-hook-form';
import {
  makeStyles,
  TextField,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {
  CapaBankCode,
  useFetchMyQuotationUserQuery,
  useCreateMyRefundAccountByQuotationUserMutation,
  CapaErrorCode
} from '@generated/graphql';
import { Stack, CustomizedButton, VStack } from '@components/Customized';
import { colorSet, useSnackbar } from '@components/Provider';
import { isBankAccountEditModeVar } from '@vars';
import { BankAccountInputGroup } from '../BankAccountInputGroup';

interface BankAccountInfo {
  bank: CapaBankCode;
  bankAccount: string;
}
const AccountAddForm = () => {
  const {
    bankAccountRegsitrationButton,
    disabledTextBold,
    label
  } = useStyles();
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const { control, errors, handleSubmit, setError } = useForm<
    BankAccountInfo
  >();
  const { enqueueSnackbar } = useSnackbar();

  const { data: myQuotationUser } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-first'
  });

  const name =
    myQuotationUser?.fetchMyQuotationUser.data?.verificationName ?? '';

  const cellphoneNumber =
    myQuotationUser?.fetchMyQuotationUser.data?.cellphoneNumber ?? '';

  // YYYY-MM-DD 10자리 문자열
  const [birthYear, birthMonth, birthDate] = (
    myQuotationUser?.fetchMyQuotationUser.data?.verificationBirthDate ??
    '0000-00-00'
  ).split('-');

  const [
    createMyRefundAccountByQuotationUser,
    { loading }
  ] = useCreateMyRefundAccountByQuotationUserMutation({
    refetchQueries: ['fetchMyRefundBankAccounts']
  });

  const onSubmit = handleSubmit((formData: BankAccountInfo) => {
    const { bankAccount, bank } = formData;

    createMyRefundAccountByQuotationUser({
      variables: {
        input: {
          account: bankAccount,
          bankCode: bank
        }
      },
      onCompleted(data) {
        if (
          data.createMyRefundAccountByQuotationUser.result ===
          CapaErrorCode.Success
        ) {
          isBankAccountEditModeVar(false);
        } else {
          setError('bank', {
            message: getErrorMessage(
              data.createMyRefundAccountByQuotationUser.result
            )
          });
        }
      },
      onError() {
        enqueueSnackbar('계좌 등록 요청에 실패했습니다.', {
          variant: 'error'
        });
      }
    });
  });

  const ResponsiveStack = matches ? VStack : Stack;

  return (
    <>
      <ResponsiveStack gap={12} width={'100%'}>
        <label className={label}>
          실명
          <TextField
            data-testid="refundAccountForm-name"
            className={disabledTextBold}
            variant="outlined"
            disabled
            value={name}
          />
        </label>
        <label className={label}>
          생년월일
          <TextField
            data-testid="refundAccountForm-birthDate"
            className={disabledTextBold}
            variant="outlined"
            disabled
            value={`${birthYear}년 ${birthMonth}월 ${birthDate}일`}
          />
        </label>
        <label className={label}>
          휴대폰 번호
          <TextField
            data-testid="refundAccountForm-cellphoneNumber"
            className={disabledTextBold}
            variant="outlined"
            disabled
            value={cellphoneNumber}
          />
        </label>
      </ResponsiveStack>

      <ResponsiveStack gap={12} width={'100%'}>
        <BankAccountInputGroup control={control} errors={errors} />
      </ResponsiveStack>

      <CustomizedButton
        data-testid="refundAccountForm-submit"
        className={bankAccountRegsitrationButton}
        variant="contained"
        size="small"
        onClick={onSubmit}
        disabled={loading}
      >
        계좌등록
      </CustomizedButton>
    </>
  );
};

export default AccountAddForm;

const useStyles = makeStyles(
  (theme) => ({
    bankAccountRegsitrationButton: {
      marginLeft: 'auto',
      width: 91,
      height: 48,
      [theme.breakpoints.down('md')]: {
        width: '100%'
      }
    },
    disabledTextBold: {
      '& .MuiOutlinedInput-root': {
        height: 48
      },
      '&.MuiOutlinedInput-root.Mui-disabled': {
        color: colorSet.gray700
      },
      '& .MuiInputBase-input': {
        color: colorSet.gray700
      }
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      width: '100%'
    },
    editButton: {
      minWidth: '84px !important',
      width: 84
    }
  }),
  { classNamePrefix: 'AccountAddForm-' }
);

const getErrorMessage = (capaErrorCode: CapaErrorCode) => {
  switch (capaErrorCode) {
    case CapaErrorCode.UnverifiedUser: {
      return '휴대폰 본인인증을 먼저 완료해주세요';
    }
    case CapaErrorCode.InvalidAccount:
    case CapaErrorCode.AccountHolderMismatch: {
      return '계좌 정보가 정확하지 않습니다.';
    }
    default: {
      return `계좌등록에 실패했습니다(errorCode: ${capaErrorCode})`;
    }
  }
};
